import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { fmt, TIER_LABELS } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [showModal, setModal]     = useState(false);
  const [editing, setEditing]     = useState(null);
  const { isManager } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await customersAPI.list({ search: search || undefined });
      setCustomers(data);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]); // eslint-disable-line

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div>
            <h2>Customers</h2>
            <div className="page-subtitle">{customers.length} customers</div>
          </div>
          {isManager && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
              + New Customer
            </button>
          )}
        </div>

        <div className="page-body">
          <div className="search-bar mb-4">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input className="search-input" placeholder="Search customers…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="card" style={{ padding: 0 }}>
            {loading ? <div className="loading-center"><div className="spinner" /></div>
            : customers.length === 0 ? (
              <div className="empty-state"><h3>No customers found</h3></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>City</th>
                      <th>Tier</th>
                      <th>Tax</th>
                      <th>Phone</th>
                      <th>Email</th>
                      {isManager && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map(c => {
                      const tr = TIER_LABELS[c.tier] || TIER_LABELS.T1;
                      return (
                        <tr key={c.id} className="clickable" onClick={() => navigate(`/customers/${c.id}`)}>
                          <td style={{ color: 'var(--text)', fontWeight: 600 }}>{c.name}</td>
                          <td>{c.city || '—'}</td>
                          <td><span className={`badge ${tr.cls}`}>{c.tier}</span></td>
                          <td>
                            {c.has_resale_cert
                              ? <span className="badge badge-green">Exempt</span>
                              : <span className="badge badge-gray">Taxable</span>}
                          </td>
                          <td className="text-muted">{c.phone || '—'}</td>
                          <td className="text-muted">{c.email || '—'}</td>
                          {isManager && (
                            <td>
                              <button className="btn btn-ghost btn-sm"
                                onClick={e => { e.stopPropagation(); setEditing(c); setModal(true); }}>
                                Edit
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <CustomerModal
          customer={editing}
          onClose={() => { setModal(false); setEditing(null); }}
          onSaved={() => { setModal(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CustomerModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:           customer?.name            || '',
    email:          customer?.email           || '',
    phone:          customer?.phone           || '',
    city:           customer?.city            || '',
    street:         customer?.street          || '',
    tier:           customer?.tier            || 'T1',
    has_resale_cert:customer?.has_resale_cert || false,
    notes:          customer?.notes           || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      if (customer) {
        await customersAPI.update(customer.id, form);
        toast.success('Customer updated');
      } else {
        await customersAPI.create(form);
        toast.success('Customer created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{customer ? 'Edit Customer' : 'New Customer'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="form-group">
          <label className="form-label">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">City</label>
            <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Tier</label>
            <select className="form-select" value={form.tier} onChange={e => set('tier', e.target.value)}>
              <option value="T1">T1 — Retail / Homeowner</option>
              <option value="T2">T2 — Builder / Contractor</option>
              <option value="T3">T3 — Developer / Volume</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Street</label>
          <input className="form-input" value={form.street} onChange={e => set('street', e.target.value)} />
        </div>
        <label className="flex items-center gap-2" style={{ cursor: 'pointer', marginBottom: 16, fontSize: 13 }}>
          <input type="checkbox" checked={form.has_resale_cert}
            onChange={e => set('has_resale_cert', e.target.checked)} />
          Has Resale Certificate (Tax Exempt)
        </label>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-textarea" rows={2} value={form.notes}
            onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="flex justify-between" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : customer ? 'Update' : 'Create Customer'}
          </button>
        </div>
      </div>
    </div>
  );
}
